/**
 * ManagerPinGate - locks the Manager Assistant behind the 4-digit PIN on every
 * fresh session. Mirrors the client PinGate UX in the manager app's green / v4
 * style.
 *
 * Behaviour:
 *   - Not authenticated            -> redirect to /pa/login.
 *   - Authenticated, PIN cached,
 *     session NOT yet unlocked      -> numpad PIN entry. Submit calls
 *                                      loginWithPin + markPinUnlocked, then
 *                                      renders children. "Use SMS instead"
 *                                      routes to /pa/login.
 *   - Otherwise                     -> render children.
 */
import { useEffect, useRef, useState, ReactNode } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { KeyRound, Loader2, Delete } from 'lucide-react';
import {
  isAuthenticated,
  getHasPinCached,
  isPinUnlocked,
  loginWithPin,
  markPinUnlocked,
  getRememberedPhone,
  getManager,
} from '@/services/api/manager-auth';
import { pa } from './base';

export default function ManagerPinGate({ children }: { children: ReactNode }) {
  const navigate = useNavigate();

  // Decide synchronously so the children never briefly flash before the gate.
  const [locked, setLocked] = useState(() => getHasPinCached() && !isPinUnlocked());
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (locked) inputRef.current?.focus();
  }, [locked]);

  // Not signed in at all -> straight to login.
  if (!isAuthenticated()) {
    return <Navigate to={pa('/login')} replace />;
  }

  const submit = async (value: string) => {
    if (!/^\d{4}$/.test(value) || busy) return;
    const phone = getRememberedPhone();
    if (!phone) {
      // No phone to verify against - punt to SMS flow.
      navigate(pa('/login'), { replace: true });
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await loginWithPin(phone, value);
      markPinUnlocked();
      setLocked(false);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Incorrect PIN');
      setPin('');
    } finally {
      setBusy(false);
    }
  };

  const onTap = (d: string) => {
    if (busy) return;
    setError(null);
    setPin(p => {
      const next = (p + d).slice(0, 4);
      if (next.length === 4) submit(next);
      return next;
    });
  };

  const onBack = () => {
    setError(null);
    setPin(p => p.slice(0, -1));
  };

  const onUseSms = () => {
    navigate(pa('/login'), { replace: true });
  };

  if (!locked) return <>{children}</>;

  const manager = getManager();
  const greeting = manager?.displayName
    ? `Welcome back, ${manager.displayName.split(' ')[0]}`
    : 'Welcome back';

  return (
    <div className="fixed inset-0 z-[90] flex flex-col items-center justify-center bg-background px-6 py-10">
      <div className="flex flex-col items-center text-center max-w-sm w-full">
        <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[var(--v4-accent)]/15 text-[var(--v4-accent)] shadow-sm">
          <KeyRound className="h-7 w-7" />
        </div>
        <h1 className="mt-5 text-lg font-semibold text-foreground">{greeting}</h1>
        <p className="mt-1 text-sm text-muted-foreground">Enter your 4-digit PIN to continue</p>

        {/* PIN dots */}
        <div className="mt-7 flex items-center gap-3">
          {[0, 1, 2, 3].map(i => {
            const filled = pin.length > i;
            return (
              <span
                key={i}
                className={
                  'h-3.5 w-3.5 rounded-full border-2 transition-all ' +
                  (filled
                    ? 'border-[var(--v4-accent)] bg-[var(--v4-accent)]'
                    : 'border-border bg-transparent')
                }
              />
            );
          })}
        </div>

        {/* Hidden input so attached keyboards work on desktop / iPad */}
        <input
          ref={inputRef}
          type="tel"
          inputMode="numeric"
          pattern="\d*"
          maxLength={4}
          value={pin}
          onChange={e => {
            const v = e.target.value.replace(/\D/g, '').slice(0, 4);
            setPin(v);
            setError(null);
            if (v.length === 4) submit(v);
          }}
          className="absolute h-0 w-0 opacity-0"
          aria-label="PIN"
          autoFocus
        />

        {error && <p className="mt-4 text-xs font-medium text-rose-600">{error}</p>}
        {busy && <Loader2 className="mt-4 h-4 w-4 animate-spin text-muted-foreground" />}

        {/* Numpad - primary input on mobile */}
        <div className="mt-7 grid grid-cols-3 gap-3 w-full max-w-[260px]">
          {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map(d => (
            <button
              key={d}
              type="button"
              onClick={() => onTap(d)}
              disabled={busy}
              className="h-14 rounded-2xl border border-border bg-card text-2xl font-semibold text-foreground active:scale-95 transition-transform disabled:opacity-50"
            >
              {d}
            </button>
          ))}
          <div />
          <button
            type="button"
            onClick={() => onTap('0')}
            disabled={busy}
            className="h-14 rounded-2xl border border-border bg-card text-2xl font-semibold text-foreground active:scale-95 transition-transform disabled:opacity-50"
          >
            0
          </button>
          <button
            type="button"
            onClick={onBack}
            disabled={busy || pin.length === 0}
            className="h-14 rounded-2xl border border-border bg-card text-muted-foreground hover:text-foreground active:scale-95 transition-transform disabled:opacity-30 flex items-center justify-center"
            aria-label="Backspace"
          >
            <Delete className="h-5 w-5" />
          </button>
        </div>

        <button
          type="button"
          onClick={onUseSms}
          disabled={busy}
          className="mt-8 text-xs font-medium text-muted-foreground hover:text-foreground underline-offset-4 hover:underline"
        >
          Use SMS code instead
        </button>
      </div>
    </div>
  );
}
